<template>
  <div
    class="md-editor"
    :class="{ 'md-dragover': dragOver }"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div class="md-toolbar">
      <button class="tool" type="button" @click="wrap('**', '**')" title="加粗">B</button>
      <button class="tool" type="button" @click="wrap('*', '*')" title="斜体"><i>I</i></button>
      <button class="tool" type="button" @click="prefix('### ')" title="标题">H</button>
      <button class="tool" type="button" @click="prefix('> ')" title="引用">❝</button>
      <button class="tool" type="button" @click="prefix('- ')" title="列表">≣</button>
      <button class="tool" type="button" @click="wrap('`', '`')" title="代码">&lt;/&gt;</button>
      <button class="tool" type="button" @click="insertLink" title="链接">🔗</button>
      <button class="tool" type="button" @click="insertMention" title="@ 提及用户">@</button>
      <button class="tool" type="button" @click="triggerUpload" title="图片">🖼</button>
      <div class="emoji-wrap">
        <button class="tool" type="button" @click="showEmoji = !showEmoji" title="表情">😀</button>
        <div v-if="showEmoji" class="emoji-pop" @mouseleave="showEmoji = false">
          <div v-for="g in emojiGroups" :key="g.name" class="emoji-group">
            <div class="emoji-group-name">{{ g.name }}</div>
            <div class="emoji-list">
              <button v-for="e in g.list" :key="e" type="button" class="emoji-item" @click="insertEmoji(e)">{{ e }}</button>
            </div>
          </div>
        </div>
      </div>
      <span class="spacer"></span>
      <button class="tool" type="button" :class="{ active: !preview }" @click="preview = false">编辑</button>
      <button class="tool" type="button" :class="{ active: preview }" @click="preview = true">预览</button>
      <input ref="fileInput" type="file" accept="image/*" hidden @change="onFile" />
    </div>

    <textarea
      v-if="!preview"
      ref="ta"
      :value="modelValue"
      :placeholder="placeholder"
      class="md-textarea"
      @input="onInput"
      @keydown="onKeyDown"
      @keyup="onKeyUp"
      @click="checkMention"
      @blur="onBlur"
      @paste="onPaste"
    ></textarea>
    <div v-else class="md-preview">
      <MarkdownBody class="md-textarea" :content="modelValue" />
    </div>

    <div v-if="uploading" class="md-uploading">图片上传中…</div>
    <div v-if="dragOver" class="md-drop-hint">🖼 松开鼠标上传图片</div>
  </div>

  <!-- @ 提及浮层：teleport 到 body，避免被 .md-editor 的 overflow:hidden 裁剪 -->
  <Teleport to="body">
    <div
      v-if="mentionOpen"
      class="mention-pop"
      :style="{ top: mentionPos.top + 'px', left: mentionPos.left + 'px' }"
    >
      <div v-if="mentionLoading && !mentionItems.length" class="mention-status">搜索中…</div>
      <div v-else-if="!mentionItems.length" class="mention-status">{{ mentionQuery ? '未找到用户' : '输入用户名搜索…' }}</div>
      <button
        v-for="(u, i) in mentionItems"
        :key="u.id"
        type="button"
        class="mention-item"
        :class="{ active: i === mentionActive }"
        @mousedown.prevent="selectMention(u)"
        @mouseenter="mentionActive = i"
      >
        <img v-if="u.avatar" class="mention-avatar" :src="u.avatar" alt="" />
        <span v-else class="mention-avatar mention-avatar-fallback">{{ (u.username || '?').charAt(0) }}</span>
        <span class="mention-name">{{ u.username }}</span>
      </button>
    </div>
  </Teleport>
</template>

<script>
import { EMOJI_GROUPS } from '@/utils/emoji'
import { uploadImage, searchUsers } from '@/api'
import { useUserStore } from '@/store/user'
import MarkdownBody from './MarkdownBody.vue'

export default {
  name: 'MarkdownEditor',
  components: { MarkdownBody },
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '分享你的想法、经验或问题。支持 Markdown 语法…' },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      emojiGroups: EMOJI_GROUPS, showEmoji: false, preview: false, uploading: false, dragOver: false, dragCounter: 0,
      // @ 提及浮层
      mentionOpen: false,
      mentionItems: [],
      mentionActive: 0,
      mentionQuery: '',
      mentionLoading: false,
      mentionStart: 0,        // 当前 @ 在文本中的下标
      mentionPos: { top: 0, left: 0 },
      mentionTimer: null,     // 搜索防抖
      mentionReqId: 0,        // 搜索请求序号，丢弃过期响应
    }
  },
  created() {
    this.userStore = useUserStore()
  },
  beforeUnmount() {
    if (this.mentionTimer) clearTimeout(this.mentionTimer)
  },
  methods: {
    // 暴露给父组件：回复时自动聚焦到输入框
    focus() {
      if (this.preview) this.preview = false
      this.$nextTick(() => this.$refs.ta?.focus())
    },
    insertAtCursor(text) {
      const ta = this.$refs.ta
      const val = this.modelValue
      if (!ta) { this.$emit('update:modelValue', val + text); return }
      const start = ta.selectionStart, end = ta.selectionEnd
      const next = val.slice(0, start) + text + val.slice(end)
      this.$emit('update:modelValue', next)
      this.$nextTick(() => {
        ta.focus()
        ta.selectionStart = ta.selectionEnd = start + text.length
      })
    },
    wrap(before, after) {
      const ta = this.$refs.ta
      if (!ta) return
      const val = this.modelValue
      const start = ta.selectionStart, end = ta.selectionEnd
      const sel = val.slice(start, end) || '文本'
      const next = val.slice(0, start) + before + sel + after + val.slice(end)
      this.$emit('update:modelValue', next)
      this.$nextTick(() => {
        ta.focus()
        ta.selectionStart = start + before.length
        ta.selectionEnd = start + before.length + sel.length
      })
    },
    prefix(p) { this.insertAtCursor('\n' + p) },
    insertLink() { this.insertAtCursor('[链接文字](https://)') },
    // 工具栏 @ 按钮：在光标处插入 @ 并弹出用户搜索浮层。
    // @ 前必须是空白才能触发提及，故光标前非空白时自动补一个空格。
    insertMention() {
      if (this.preview) this.preview = false
      this.$nextTick(() => {
        const ta = this.$refs.ta
        if (!ta) return
        ta.focus()
        const val = ta.value
        const caret = ta.selectionStart
        const charBefore = caret > 0 ? val[caret - 1] : '\n'
        this.insertAtCursor(/\s/.test(charBefore) ? '@' : ' @')
        this.$nextTick(() => this.checkMention())
      })
    },
    insertEmoji(e) { this.insertAtCursor(e); this.showEmoji = false },
    triggerUpload() { this.$refs.fileInput.click() },
    // 复用同一套上传+插入逻辑：工具栏点选、Ctrl/Cmd+V 粘贴都走这里
    async uploadAndInsert(file) {
      if (!file) return
      this.uploading = true
      try {
        const res = await uploadImage(file)
        this.insertAtCursor(`\n![图片](${res.data.url})\n`)
      } catch (e) {
        await this.$alert({ type: 'error', message: e.message || '上传失败' })
      } finally {
        this.uploading = false
      }
    },
    async onFile(ev) {
      const file = ev.target.files[0]
      if (!file) return
      await this.uploadAndInsert(file)
      ev.target.value = ''
    },
    // 直接粘贴图片：从剪贴板取图片文件上传；普通文本粘贴不受影响
    async onPaste(ev) {
      const items = ev.clipboardData?.items
      if (!items) return
      const images = []
      for (const it of items) {
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const f = it.getAsFile()
          if (f) images.push(f)
        }
      }
      if (!images.length) return            // 非图片粘贴：交给浏览器默认处理
      ev.preventDefault()                    // 阻止把图片二进制/文件名插进文本框
      for (const f of images) await this.uploadAndInsert(f)
    },
    // 拖拽图片进编辑框：dragover 必须 preventDefault，否则浏览器会直接打开文件而不触发 drop
    isFileDrag(ev) {
      const types = ev.dataTransfer?.types
      return Array.from(types || []).includes('Files')
    },
    onDragEnter(ev) {
      if (!this.isFileDrag(ev)) return
      ev.preventDefault()
      this.dragCounter++
      this.dragOver = true
    },
    onDragOver(ev) {
      if (!this.isFileDrag(ev)) return
      ev.preventDefault()
      ev.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave() {
      // 用计数器抵消子元素间切换触发的 dragleave，避免高亮闪烁
      this.dragCounter = Math.max(0, this.dragCounter - 1)
      if (this.dragCounter === 0) this.dragOver = false
    },
    async onDrop(ev) {
      ev.preventDefault()
      this.dragCounter = 0
      this.dragOver = false
      const files = ev.dataTransfer?.files
      if (!files || !files.length) return
      const images = []
      for (const f of files) {
        if (f.type.startsWith('image/')) images.push(f)
      }
      if (!images.length) return            // 非图片文件：静默丢弃，不插入
      for (const f of images) await this.uploadAndInsert(f)
    },
    onInput(e) {
      this.$emit('update:modelValue', e.target.value)
      this.checkMention()
    },
    // ===== @ 提及 =====
    onKeyDown(e) {
      if (!this.mentionOpen) return
      if (e.key === 'Escape') { e.preventDefault(); this.closeMention(); return }
      if (!this.mentionItems.length) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.mentionActive = (this.mentionActive + 1) % this.mentionItems.length
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.mentionActive = (this.mentionActive - 1 + this.mentionItems.length) % this.mentionItems.length
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const item = this.mentionItems[this.mentionActive]
        if (item) this.selectMention(item)
      }
    },
    onKeyUp(e) {
      // 方向键 / Home / End 移动光标后，重判 @ 上下文
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) this.checkMention()
    },
    onBlur() {
      // 延迟关闭，让浮层 mousedown 先选中
      setTimeout(() => this.closeMention(), 150)
    },
    // 检测光标前是否有合法的 @query，决定是否弹出浮层
    checkMention() {
      const ta = this.$refs.ta
      if (!ta || this.preview) return
      const val = ta.value
      const caret = ta.selectionStart
      const before = val.slice(0, caret)
      const atIdx = before.lastIndexOf('@')
      if (atIdx < 0) { this.closeMention(); return }
      // @ 前必须是行首或空白（排除邮箱 a@b、行内 xxx@yyy）
      const charBefore = atIdx > 0 ? before[atIdx - 1] : '\n'
      if (!/\s/.test(charBefore)) { this.closeMention(); return }
      // @ 到光标之间为查询串，不能含空白（含空白则 @ 已结束）
      const query = before.slice(atIdx + 1)
      if (/\s/.test(query) || query.length > 30) { this.closeMention(); return }
      this.mentionStart = atIdx
      this.positionMention()
      if (!this.mentionOpen) {
        this.mentionOpen = true
        this.mentionActive = 0
        this.mentionItems = []
      }
      this.scheduleSearch(query)
    },
    scheduleSearch(query) {
      this.mentionQuery = query
      if (this.mentionTimer) clearTimeout(this.mentionTimer)
      if (query === '') { this.mentionItems = []; return }
      this.mentionTimer = setTimeout(() => this.doSearch(query), 200)
    },
    async doSearch(query) {
      this.mentionReqId++
      const reqId = this.mentionReqId
      this.mentionLoading = true
      try {
        const res = await searchUsers(query)
        if (reqId !== this.mentionReqId) return
        const myId = this.userStore.user ? Number(this.userStore.user.id) : 0
        this.mentionItems = (res.data || []).filter((u) => Number(u.id) !== myId)
        this.mentionActive = 0
      } catch (e) {
        if (reqId === this.mentionReqId) this.mentionItems = []
      } finally {
        if (reqId === this.mentionReqId) this.mentionLoading = false
      }
    },
    selectMention(item) {
      const ta = this.$refs.ta
      const val = ta ? ta.value : this.modelValue
      const atIdx = this.mentionStart
      const caret = ta ? ta.selectionStart : val.length
      const insert = `[@${item.username}](/u/${item.id}) `
      const next = val.slice(0, atIdx) + insert + val.slice(caret)
      this.$emit('update:modelValue', next)
      this.closeMention()
      this.$nextTick(() => {
        if (!ta) return
        const pos = atIdx + insert.length
        ta.focus()
        ta.selectionStart = ta.selectionEnd = pos
      })
    },
    closeMention() {
      if (this.mentionTimer) clearTimeout(this.mentionTimer)
      this.mentionReqId++   // 使 in-flight 请求失效
      this.mentionOpen = false
      this.mentionItems = []
      this.mentionActive = 0
      this.mentionQuery = ''
    },
    positionMention() {
      const c = this.getCaretCoords()
      if (!c) return
      const popH = 220, popW = 240
      let top = c.top + 18
      if (top + popH > window.innerHeight) top = c.top - popH - 6
      let left = c.left
      if (left + popW > window.innerWidth) left = window.innerWidth - popW - 8
      this.mentionPos = { top: Math.max(4, top), left: Math.max(4, left) }
    },
    // 镜像 div 法：复制 textarea 样式，量出光标像素坐标（视口相对）
    getCaretCoords() {
      const ta = this.$refs.ta
      if (!ta) return null
      const val = ta.value
      const caret = ta.selectionStart
      const style = getComputedStyle(ta)
      const div = document.createElement('div')
      const copyProps = ['direction', 'boxSizing', 'color', 'background', 'borderStyle', 'borderWidth', 'borderColor', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize', 'lineHeight', 'fontFamily', 'textAlign', 'textTransform', 'textIndent', 'textDecoration', 'letterSpacing', 'wordSpacing', 'tabSize', 'whiteSpace', 'wordWrap', 'wordBreak']
      copyProps.forEach((p) => { try { div.style[p] = style[p] } catch (e) { /* ignore */ } })
      const padL = parseFloat(style.paddingLeft) || 0
      const padR = parseFloat(style.paddingRight) || 0
      div.style.boxSizing = 'content-box'
      div.style.width = (ta.clientWidth - padL - padR) + 'px'
      div.style.position = 'absolute'
      div.style.visibility = 'hidden'
      div.style.whiteSpace = 'pre-wrap'
      div.style.wordWrap = 'break-word'
      div.textContent = val.slice(0, caret)
      const span = document.createElement('span')
      span.textContent = val.slice(caret) || '.'
      div.appendChild(span)
      document.body.appendChild(div)
      const taRect = ta.getBoundingClientRect()
      const divRect = div.getBoundingClientRect()
      const spanRect = span.getBoundingClientRect()
      document.body.removeChild(div)
      return {
        top: taRect.top + (spanRect.top - divRect.top) - ta.scrollTop,
        left: taRect.left + (spanRect.left - divRect.left) - ta.scrollLeft,
      }
    },
  },
}
</script>

<style scoped>
.md-editor { position: relative; border: 1px solid var(--border-strong); border-radius: var(--radius-sm); overflow: hidden; }
.md-editor.md-dragover { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-soft); }
.md-drop-hint {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: rgba(99, 102, 241, .10); color: var(--primary);
  font-size: 15px; font-weight: 600; pointer-events: none;
}
.md-toolbar {
  display: flex; align-items: center; gap: 2px; flex-wrap: wrap;
  padding: 6px 8px; background: var(--surface-2); border-bottom: 1px solid var(--border);
}
.tool {
  min-width: 30px; height: 30px; padding: 0 8px; border-radius: 6px;
  color: var(--text-2); font-size: 14px; transition: background .15s, color .15s;
}
.tool:hover { background: var(--surface); color: var(--text); }
.tool.active { background: var(--primary-soft); color: var(--primary); }
.spacer { flex: 1; }
.md-textarea {
  width: 100%; min-height: 160px; max-height: 420px; overflow-y: auto;
  border: none; outline: none; resize: vertical;
  padding: 14px; font-size: 15px; line-height: 1.7; background: var(--surface);
}
.emoji-wrap { position: relative; }
.emoji-pop {
  position: absolute; top: 34px; left: 0; z-index: 30; width: 280px;
  background: var(--surface); border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm); box-shadow: var(--shadow-lg); padding: 10px; max-height: 240px; overflow-y: auto;
}
.emoji-group-name { font-size: 12px; color: var(--text-3); margin: 6px 0 4px; }
.emoji-list { display: grid; grid-template-columns: repeat(8, 1fr); gap: 2px; }
.emoji-item { height: 30px; font-size: 18px; border-radius: 6px; }
.emoji-item:hover { background: var(--surface-2); }
.md-uploading { padding: 6px 14px; font-size: 13px; color: var(--primary); background: var(--primary-soft); }

/* @ 提及浮层（teleport 到 body，position: fixed）*/
.mention-pop {
  position: fixed; z-index: 1000; min-width: 200px; max-width: 260px;
  background: var(--surface); border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm); box-shadow: var(--shadow-lg);
  padding: 6px; max-height: 260px; overflow-y: auto;
}
.mention-status { padding: 10px 12px; font-size: 13px; color: var(--text-3); text-align: center; }
.mention-item {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 7px 10px; border: none; background: none; border-radius: 6px;
  cursor: pointer; text-align: left;
}
.mention-item:hover, .mention-item.active { background: var(--surface-2); }
.mention-avatar { width: 26px; height: 26px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.mention-avatar-fallback {
  background: var(--primary-soft); color: var(--primary);
  font-size: 13px; font-weight: 600;
  display: flex; align-items: center; justify-content: center;
}
.mention-name { font-size: 14px; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
